import {
  AttachmentMalwareScanStatus,
  fetchFile,
  IFile,
  isUploadCancelled,
  UploadOptions
} from '@query'
import { QueryClient } from '@tanstack/react-query'
import { DefaultTheme } from 'styled-components'
import toast from 'react-hot-toast'
import { UploadToast, UploadToastProps } from './UploadToast'
import { canEditAttachmentNotes, UPLOAD_TOAST_DURATION_MS } from '@utils/attachmentNotes'
import { getUploadFileToastOptions } from './uploadFileToastStyle'

const SCAN_POLL_MS = 2000
const SCAN_TIMEOUT_MS = 15 * 60 * 1000

const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms))

type ToastState = Pick<UploadToastProps, 'phase' | 'fileId' | 'progress' | 'onCancel'>

export interface UploadFileToastOptions {
  slug: string
  canEditNotes: boolean
  queryClient: QueryClient
  theme: DefaultTheme
}

/** Resolves to null when the user cancels the upload. */
export const uploadFileWithToast = async (
  file: File,
  startUpload: (options: Required<UploadOptions>) => Promise<IFile>,
  { slug, canEditNotes, queryClient, theme }: UploadFileToastOptions
): Promise<IFile | null> => {
  const toastOptions = getUploadFileToastOptions(theme)
  const abortController = new AbortController()
  const onCancel = () => abortController.abort()
  let toastId: string | undefined

  const show = (showToast: typeof toast.loading, state: ToastState, duration?: number) => {
    toastId = showToast(
      ({ id }) => (
        <UploadToast
          file={file}
          toastId={id}
          slug={slug}
          canEditNotes={canEditAttachmentNotes(canEditNotes)}
          queryClient={queryClient}
          {...state}
        />
      ),
      { ...toastOptions, id: toastId, duration }
    )
  }

  const showDone = (uploadedFile: IFile) =>
    show(
      toast.success,
      { phase: 'done', fileId: uploadedFile?.id },
      uploadedFile?.id != null && canEditAttachmentNotes(canEditNotes)
        ? Infinity
        : UPLOAD_TOAST_DURATION_MS
    )

  const followMalwareScan = async (uploadedFile: IFile) => {
    const deadline = Date.now() + SCAN_TIMEOUT_MS

    while (Date.now() < deadline) {
      await wait(SCAN_POLL_MS)
      try {
        const latest = await fetchFile({ slug, fileId: uploadedFile.id })
        if (latest.malware_scan_status === AttachmentMalwareScanStatus.PENDING) continue

        await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
        if (latest.malware_scan_status === AttachmentMalwareScanStatus.INFECTED) {
          show(toast.error, { phase: 'infected' }, UPLOAD_TOAST_DURATION_MS)
        } else {
          showDone(latest)
        }
        return
      } catch {
        // The file is already stored. Keep asking until the scan deadline.
      }
    }

    showDone(uploadedFile)
  }

  show(toast.loading, { phase: 'uploading', progress: 0, onCancel })

  let uploadedFile: IFile
  try {
    uploadedFile = await startUpload({
      onProgress: ({ loaded, total }) => {
        if (total > 0)
          show(toast.loading, { phase: 'uploading', progress: loaded / total, onCancel })
      },
      signal: abortController.signal
    })
  } catch (error) {
    if (isUploadCancelled(error)) {
      toast.dismiss(toastId)
      return null
    }
    show(toast.error, { phase: 'failed' })
    throw error
  }

  if (
    uploadedFile?.id != null &&
    uploadedFile.malware_scan_status === AttachmentMalwareScanStatus.PENDING
  ) {
    show(toast.loading, { phase: 'scanning', fileId: uploadedFile.id }, Infinity)
    void followMalwareScan(uploadedFile)
  } else {
    showDone(uploadedFile)
  }
  return uploadedFile
}
